import { DatePipe, KeyValue } from '@angular/common';
import { Injectable } from '@angular/core';
import { FormArray, FormControl, FormGroup, ValidatorFn } from '@angular/forms';
import { createMask } from '@ngneat/input-mask';
import { saveAs } from 'file-saver';
import { assign, each, find, isEmpty, isNil, isObject } from 'lodash';

import { Vendor } from '../classes/models';
import { File as FileModel } from '../classes/models/file';
import { VendorTrade } from '../classes/models/vendor-trade';
import { NotificationBanner } from '../interfaces/banner/notification-banner';
import { Card } from '../interfaces/card';
import { GenericObject } from '../interfaces/generic-object';
import { LocalizationInfo } from '../interfaces/localization-info';
import { NotificationRecipient } from '../interfaces/models/notification-recipient';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root'
})
export class UtilityService {
  constructor(
    private ToastService: ToastService,
    private datePipe: DatePipe
  ) {}

  // This function takes a country as an input and provides a map of localized values (for the input country)
  getLocalizedValuesMap(country: 'US' | 'CA') {
    if (country === 'US') {
      const localizedValuesMap: LocalizationInfo = {
        taxId: (type) => {
          if (type === 'ein') {
            const map: { type: 'ein'; label: 'EIN'; format: '__-_______' } = {
              type,
              label: 'EIN',
              format: '__-_______'
            };
            return map;
          } else if (type === 'ssn') {
            const map: { type: 'ssn'; label: 'SSN'; format: '___-__-____' } = {
              type,
              label: 'SSN',
              format: '___-__-____'
            };
            return map;
          } else {
            throw new Error('Tax id type is not correct for localization.');
          }
        },
        legalName: { label: 'Entity Name as it appears on Line 1 of your W-9' },
        dba: { label: 'DBA' },
        registrationDba: {
          label:
            'Business Name/Disregarded Entity/DBA (If different than Entity Name)'
        },
        country: { label: 'US' },
        countryFull: { label: 'United States' },
        territory: { label: 'State' },
        territoryPlural: { label: 'States' },
        territoryLineBreak: { label: 'State' },
        territoryLineBreakPlural: { label: 'States' },
        stateProvincePlural: { label: 'States' },
        territoryOptional: { label: 'State(s)' },
        socialSecurity: { label: 'SSN' },
        workersInsurance: {
          label: (territory?) => "Worker's Compensation"
        },
        postalCode: { label: 'Zip Code', format: '_____' },
        moneySymbol: { label: '$' },
        currencyCode: { label: 'USD' }
      };

      return localizedValuesMap;
    } else if (country === 'CA') {
      const localizedValuesMap: LocalizationInfo = {
        taxId: (type) => {
          if (type === 'gst-hst') {
            const map: {
              type: 'gst-hst';
              label: 'GST/HST';
              format: '_________';
            } = {
              type,
              label: 'GST/HST',
              format: '_________'
            };
            return map;
          } else {
            throw new Error('Tax id type is not correct for localization.');
          }
        },
        legalName: { label: 'Legal Name' },
        dba: { label: 'd/b/a' },
        registrationDba: { label: 'd/b/a' },
        country: { label: 'CA' },
        countryFull: { label: 'Canada' },
        territory: { label: 'Province/Territory' },
        territoryPlural: { label: 'Provinces/Territories' },
        territoryLineBreak: { label: 'Province/\nTerritory' },
        territoryLineBreakPlural: { label: 'Provinces/\nTerritories' },
        stateProvincePlural: { label: 'Provinces' },
        territoryOptional: { label: 'Province(s)' },
        socialSecurity: { label: 'SIN' },
        workersInsurance: {
          label: (territory?) => {
            if (territory === 'AB') {
              return 'WCB';
            }
            return 'WSIB';
          }
        },
        postalCode: { label: 'Postal Code', format: '___ ___' },
        moneySymbol: { label: 'CAD' },
        currencyCode: { label: 'CAD' }
      };

      return localizedValuesMap;
    } else {
      throw new Error('The input country is not supported for localization.');
    }
  }

  mapValuesToCard(
    title: string | null,
    bodyText: string | null,
    record: any = undefined,
    isSelected = false
  ): Card {
    const card: Card = {
      title,
      bodyText,
      record,
      isSelected
    };
    return card;
  }

  mapValuesToNotificationBanner(
    type: string,
    title: string | null,
    bodyText: string | null,
    notificationRecipient: NotificationRecipient
  ): NotificationBanner {
    const notificationBanner: NotificationBanner = {
      type,
      title,
      bodyText,
      NotificationRecipient: notificationRecipient
    };
    return notificationBanner;
  }

  // Used to retrieve regular expressions for various components. Centralized, so if it's changed here
  // it's changed everywhere that uses the regex retrieved from this function.
  getRegex(type: string) {
    const typeToRegexMap: {
      [key: string]: RegExp;
    } = {
      email:
        /^[-!#$%&'*+\/0-9=?A-Z^_a-z{|}~()](\.?[-!#$%&'*+\/0-9=?A-Z^_a-z`{|}~()])*@[a-zA-Z0-9](-*\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/
    };

    const regex = typeToRegexMap[type];
    if (regex) {
      return regex;
    }
    throw new Error('Regular Expression does not exist.');
  }

  convertArrayBufferToBase64EncodedString(arrayBuffer: ArrayBuffer): string {
    if (!arrayBuffer) {
      throw new Error(
        'Cannot convert Array Buffer to Base 64 Encoded String. Missing required parameter: arrayBuffer.'
      );
    }
    const UInt8Array = new Uint8Array(arrayBuffer);

    const base64EncodedArray: string[] = [];
    each(UInt8Array, (data) => {
      base64EncodedArray.push(String.fromCharCode(data));
    });

    return window.btoa(base64EncodedArray.join(''));
  }

  convertBase64StringToArrayBuffer(base64String: string): ArrayBuffer {
    const binaryString = window.atob(base64String);
    const length = binaryString.length;
    const buffer = new ArrayBuffer(length);
    const array = new Uint8Array(buffer);

    for (let i = 0; i < length; i++) {
      array[i] = binaryString.charCodeAt(i);
    }

    return buffer;
  }

  downloadFile(File: FileModel) {
    const { filename, filetype, FileContent } = File;

    if (!filename) {
      throw this.ToastService.addErrorToast('Error', 'File name is missing', {
        returnError: true
      });
    }

    if (!FileContent || !FileContent.filecontents) {
      throw this.ToastService.addErrorToast(
        'Error',
        'File contents is missing.',
        { returnError: true }
      );
    }

    if (!filetype) {
      throw this.ToastService.addErrorToast('Error', 'File type is missing.', {
        returnError: true
      });
    }

    const { filecontents } = FileContent;

    saveAs(`data:${filetype};base64,${filecontents}`, filename);
  }

  isVendor(vendor: any): vendor is Vendor {
    return vendor instanceof Vendor;
  }

  // Consider using this instead of isValidationErrors above. The above function doesn't check for deeply nested
  // AbstractControls.
  // This function also scrolls the browser to the first form control with an error.
  generateValidationErrorsObject(
    control: FormGroup | FormArray | FormControl,
    errors: { [key: string]: any } = {},
    FoundFirstFormControlError: { FirstFormControlError?: FormControl } = {}
  ): { [key: string]: any } | null {
    if (control.errors) {
      // errors[controlName] = control.errors;
      assign(errors, control.errors);
      control.markAsDirty();

      if (
        control instanceof FormControl &&
        !FoundFirstFormControlError.FirstFormControlError
      ) {
        const { nativeElement } = control as FormControl & {
          nativeElement: HTMLElement;
        };
        if (
          nativeElement &&
          typeof nativeElement.scrollIntoView === 'function'
        ) {
          nativeElement.scrollIntoView({ behavior: 'auto', block: 'center' });
        }
        Object.assign(FoundFirstFormControlError, {
          FirstFormControlError: control
        });
      }
    }

    if (control instanceof FormGroup || control instanceof FormArray) {
      if (control.controls) {
        each(
          control.controls as {
            [key: string]: FormGroup | FormArray | FormControl;
          },
          (nestedControl: FormGroup | FormArray | FormControl, key: string) => {
            const nestedErrors = this.generateValidationErrorsObject(
              nestedControl,
              {},
              FoundFirstFormControlError
            );
            if (nestedErrors) {
              assign(errors, { [key]: nestedErrors });
            }
          }
        );
      }
    }

    return isEmpty(errors) ? null : errors;
  }

  // This function is used to disable the form controls in a form group. If a collection of
  // validator functions are passed in, form controls with the validator functions are disabled.
  recursivelyDisableFormControls(
    formControl: FormGroup | FormArray,
    options: { validatorsToDisable: ValidatorFn[] } = {
      validatorsToDisable: []
    }
  ) {
    if (!formControl) {
      throw new Error(
        'Cannot disable form group controls. Missing form control.'
      );
    }

    const { validatorsToDisable } = options;

    each(
      formControl.controls as (FormControl | FormGroup | FormArray)[],
      (control) => {
        if (validatorsToDisable.length === 0) {
          control.disable();
        }

        const doesControlHaveValidator = !isNil(
          find(validatorsToDisable, (validatorFn) =>
            control.hasValidator(validatorFn)
          )
        );
        if (
          validatorsToDisable.length > 0 &&
          control.validator &&
          doesControlHaveValidator
        ) {
          control.disable();
        }

        // Unneeded, controls are already recursively disabled.
        // if ((control instanceof FormArray) || (control instanceof FormGroup)) {
        //   this.recursivelyDisableFormControls(control, options);
        // }
      }
    );
  }

  generateConcatenatedTradesAndTradeCategories(vendorTrades: VendorTrade[]): {
    tradesConcatenated: string;
    tradeCategoriesConcatenated: string;
  } {
    const tradeCategoryNames: Set<string> = new Set([]);
    const tradeNames: string[] = [];
    const concatenatedTradesAndTradeCategories = {
      tradesConcatenated: '',
      tradeCategoriesConcatenated: ''
    };

    each(vendorTrades, (vt) => {
      const { PrimaryTrade } = vt;
      if (!PrimaryTrade) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Vendor Trade is missing Primary Trade.',
          { returnError: true }
        );
      }
      const { TradeCategories, name } = PrimaryTrade;

      if (!name) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Primary Trade does not have a name.',
          { returnError: true }
        );
      }

      tradeNames.push(this.formatVendorTradeDisplayName(vt));

      if (TradeCategories) {
        each(TradeCategories, (tc) => {
          const { categoryName } = tc;

          if (!categoryName) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Trade Category is missing name.',
              { returnError: true }
            );
          }

          if (!tradeCategoryNames.has(categoryName)) {
            tradeCategoryNames.add(categoryName);
          }
        });
      }
    });

    assign(concatenatedTradesAndTradeCategories, {
      tradesConcatenated: tradeNames.join(', '),
      tradeCategoriesConcatenated: [...tradeCategoryNames].join(', ')
    });

    return concatenatedTradesAndTradeCategories;
  }

  // This takes an object for querying the backend, and stringifies all objects
  // within the object.
  stringifyObjectsInQueryObject(queryObject: { [key: string]: any }) {
    Object.keys(queryObject).forEach((property) => {
      const value = queryObject[property];
      if (isObject(value)) {
        Object.assign(queryObject, { [property]: JSON.stringify(value) });
      }
    });
    return queryObject;
  }

  // Still experimental. Watch closely.
  // Reverses the effects of stringifyObjectsInQueryObject.
  parseStringsInQueryObject(queryObject: GenericObject) {
    const Properties = Object.keys(queryObject);
    while (Properties.length > 0) {
      try {
        const property = Properties.shift()!;

        const value = queryObject[property];
        if (typeof value === 'string') {
          const parsedValue = JSON.parse(value);
          Object.assign(queryObject, {
            [property]: isObject(parsedValue) ? parsedValue : value
          });
        }
      } catch (error) {
        // If an error occurs due to parsing, do nothing.
      }
    }
    return queryObject;
  }

  formatDate(date: Date | undefined | null): string {
    return date ? this.datePipe.transform(date, 'yyyy-MM-dd')! : '';
  }

  // This is similar to the lodash 'pick' method. It accepts an object and an array of strings
  // then composes a new object containing the properties/values from the original object. We do this by
  // iterating through the array of strings and applying the values from the original object as accessed
  // properties from the string array iteration.
  // The 'clone' parameter will clone the data assigned to the newly composed object. Marking 'clone' as false
  // will assign the values by reference.
  constructObjectFromObjectProperties<type>(
    sourceObject: { [key: string]: any },
    properties: string[],
    clone: boolean = true
  ): type {
    const targetObject: { [key: string]: any } = {};
    properties.forEach((p) => {
      const sourceValue = sourceObject[p];
      if (sourceValue !== undefined) {
        targetObject[p] = clone
          ? JSON.parse(JSON.stringify(sourceValue))
          : sourceValue;
      }
    });
    return targetObject as type;
  }

  // This function creates a 2-dimensional array. It accepts a source array (the array to transform)
  // as a parameter, and the max elements each array should contain (a number) as the second element.
  // For example, if a source array of 100 elements is passed and the max elements per array is 20,
  // this function will return an array of 5 arrays with each sub-array containing 20 elements. If an array
  // of 110 elements is passed with max elements set to 20, this function will return an array containing 6
  // arrays, with 5 arrays containing 20 elements and the 6th array containing 10 elements.
  constructMultidimensionalArrayFromArray = <Type>(
    sourceArray: Type[],
    maxElementsPerArray: number
  ): Type[][] => {
    return sourceArray.reduce((accumulator, value, index) => {
      if (
        !accumulator[accumulator.length - 1] ||
        accumulator[accumulator.length - 1].length === maxElementsPerArray
      ) {
        accumulator.push([]);
      }

      accumulator[accumulator.length - 1].push(value);

      return accumulator;
    }, [] as Type[][]);
  };

  copyUrl(url: string | null) {
    if (!url) {
      throw this.ToastService.addErrorToast('Error', 'No URL to copy.');
    }

    navigator.clipboard
      .writeText(url)
      .then(() =>
        this.ToastService.addSuccessToast('Success', 'Copied URL to clipboard.')
      )
      .catch((err) =>
        this.ToastService.addErrorToast('Error', err, { returnError: true })
      );
  }

  setCoverageAmounts(
    startingAmount: number,
    increment: number,
    maxValue: number,
    startingCoverageAmountsMap?: Map<string, number | null>,
    endingCoverageAmountsMap?: Map<string, number | null>
  ): Map<string, number | null> {
    const coverageAmountMap = new Map<string, number | null>([
      ...(startingCoverageAmountsMap?.entries() || [])
    ]);

    const newEntries: [string, number][] = [];
    for (let i = startingAmount; i <= maxValue; i += increment) {
      newEntries.push([`$${i.toLocaleString()}`, i]);
    }
    newEntries.forEach(([key, value]) => coverageAmountMap.set(key, value));

    if (endingCoverageAmountsMap) {
      endingCoverageAmountsMap.forEach((value, key) =>
        coverageAmountMap.set(key, value)
      );
    }

    return coverageAmountMap;
  }

  private formatVendorTradeDisplayName(vendorTrade: {
    PrimaryTrade?: { name?: string };
    otherTradeDescription?: string | null;
  }): string {
    const name = vendorTrade.PrimaryTrade?.name?.trim() ?? '';
    const description = vendorTrade.otherTradeDescription?.trim();

    if (name === 'Other' && description) {
      return `Other — "${description}"`;
    }

    return name;
  }

  keyValuePairDefaultOrder(
    a: KeyValue<any, any>,
    b: KeyValue<any, any>
  ): number {
    return 0;
  }

  preventInput(event: KeyboardEvent) {
    const { key } = event;

    if (key !== 'Tab') {
      return false;
    }

    return event;
  }

  // Input Masks
  phoneMask = createMask('(999) 999-9999');
  // US Tax Id Masks
  einMask = createMask('99-9999999');
  ssnMask = createMask('999-99-9999');

  // Canadian Tax Id Mask
  gstHstMask = createMask('999999999');

  usZipMask = createMask('99999');
  canadianZipMask = createMask('A9A 9A9');
}
